import React, { useState, useEffect } from "react";
import { SeoAnalysisReport } from "./seoAnalyzer";
import { Header } from "./components/Header";
import { ScannerInput } from "./components/ScannerInput";
import { LoadingHUD } from "./components/LoadingHUD";
import { ScoreGauges } from "./components/ScoreGauges";
import { MetricsQuickBar } from "./components/MetricsQuickBar";
import { AuditFeed } from "./components/AuditFeed";
import { SocialPreview } from "./components/SocialPreview";
import { RawMetadataViewer } from "./components/RawMetadataViewer";
import { ReportActions } from "./components/ReportActions";
import { AlertCircle, ListChecks, Share2, Code2, Sparkles, Terminal, Search, ShieldCheck, Zap } from "lucide-react";
import "./index.css";

export function App() {
  const [inputUrl, setInputUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<SeoAnalysisReport | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<"audits" | "social" | "raw">("audits");

  // Load scan history from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem("cyber_seo_history");
      if (saved) {
        setHistory(JSON.parse(saved));
      }
    } catch {
      // Ignored
    }
  }, []);

  const handleScan = async (targetUrlOverride?: string) => {
    const target = targetUrlOverride || inputUrl;
    if (!target.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: target }),
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        throw new Error(data.error || "Failed to scan website.");
      }

      setReport(data);

      // Update history
      setHistory((prev) => {
        const updated = [data.url, ...prev.filter((item) => item !== data.url)].slice(0, 5);
        try {
          localStorage.setItem("cyber_seo_history", JSON.stringify(updated));
        } catch {
          // Ignored
        }
        return updated;
      });
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred during matrix scan.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container">
      <Header />

      {/* URL Scanner Input */}
      <ScannerInput
        inputUrl={inputUrl}
        setInputUrl={setInputUrl}
        onScan={handleScan}
        loading={loading}
        history={history}
      />

      {/* Error Banner */}
      {error && (
        <div
          className="cyber-card cyber-card-crimson"
          style={{ marginBottom: "2rem", display: "flex", alignItems: "center", gap: "0.85rem", borderColor: "var(--neon-crimson)" }}
        >
          <AlertCircle size={24} color="var(--neon-crimson)" />
          <div>
            <h4 style={{ color: "var(--neon-crimson)", fontSize: "0.95rem" }}>SCAN FAILURE DISPATCH</h4>
            <p style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>{error}</p>
          </div>
        </div>
      )}

      {/* Loading HUD state */}
      {loading && <LoadingHUD targetUrl={inputUrl} />}

      {/* Empty State / Welcome Screen when no report generated yet */}
      {!loading && !report && (
        <div className="cyber-card" style={{ padding: "3.5rem 2rem", textAlign: "center", margin: "2rem 0" }}>
          <div
            style={{
              width: "70px",
              height: "70px",
              margin: "0 auto 1.5rem",
              borderRadius: "50%",
              background: "rgba(0, 243, 255, 0.1)",
              border: "1px solid var(--neon-cyan)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--neon-cyan)",
              boxShadow: "0 0 25px rgba(0, 243, 255, 0.3)",
            }}
          >
            <Search size={32} />
          </div>

          <h2 style={{ fontSize: "1.4rem", color: "var(--neon-cyan)", marginBottom: "0.5rem" }}>
            AWAITING TARGET WEBSITE URL
          </h2>
          <p style={{ color: "var(--text-secondary)", maxWidth: "540px", margin: "0 auto 2rem", fontSize: "0.9rem" }}>
            Enter any public website domain in the input bar above to run real-time SEO audit diagnostics, Lighthouse Core Web Vitals, Open Graph previews, and AI code fix suggestions.
          </p>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "1.5rem",
              flexWrap: "wrap",
              fontFamily: "var(--font-mono)",
              fontSize: "0.8rem",
              color: "var(--text-muted)",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
              <ShieldCheck size={14} color="var(--neon-emerald)" /> LIGHTHOUSE ENGINE ACTIVE
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
              <Zap size={14} color="var(--neon-cyan)" /> AST METADATA PARSER
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
              <Sparkles size={14} color="var(--neon-purple)" /> REALTIME AI FIX RECOMMENDATIONS
            </span>
          </div>
        </div>
      )}

      {/* Results Dashboard */}
      {!loading && report && (
        <div>
          {/* Top Bar showing domain + timestamp + report actions */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem", flexWrap: "wrap", gap: "1rem" }}>
            <div>
              <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)", fontFamily: "var(--font-mono)" }}>
                TARGET DOMAIN AUDITED
              </div>
              <h2 style={{ fontSize: "1.6rem", color: "var(--neon-cyan)", letterSpacing: "0.05em" }}>
                {report.domain}
              </h2>
              <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", fontFamily: "var(--font-mono)", marginTop: "0.2rem" }}>
                Scanned: {new Date(report.timestamp).toLocaleString()} {report.metrics.lighthouseUsed && " | Chromium Engine ✓"}
              </div>
            </div>

            <ReportActions report={report} onRescan={() => handleScan(report.url)} />
          </div>

          {/* Overall Gauges & Subcategory Scores */}
          <ScoreGauges report={report} />

          {/* Quick Metrics Bar */}
          <MetricsQuickBar metrics={report.metrics} />

          {/* Navigation Tabs */}
          <div className="tabs-nav">
            <button
              className={`tab-btn ${activeTab === "audits" ? "active" : ""}`}
              onClick={() => setActiveTab("audits")}
            >
              <ListChecks size={16} />
              <span>AUDIT DIAGNOSTICS ({report.audits.length})</span>
            </button>

            <button
              className={`tab-btn ${activeTab === "social" ? "active" : ""}`}
              onClick={() => setActiveTab("social")}
            >
              <Share2 size={16} />
              <span>SOCIAL GRAPH CARD</span>
            </button>

            <button
              className={`tab-btn ${activeTab === "raw" ? "active" : ""}`}
              onClick={() => setActiveTab("raw")}
            >
              <Code2 size={16} />
              <span>HTML METADATA & SCHEMAS</span>
            </button>
          </div>

          {/* Tab Content Panels */}
          {activeTab === "audits" && <AuditFeed audits={report.audits} />}
          {activeTab === "social" && <SocialPreview meta={report.meta} domain={report.domain} />}
          {activeTab === "raw" && <RawMetadataViewer meta={report.meta} metrics={report.metrics} />}
        </div>
      )}

      {/* Footer */}
      <footer className="cyber-footer">
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Sparkles size={14} color="var(--neon-cyan)" />
          <span>CYBER // SEO INTEL ENGINE &copy; {new Date().getFullYear()}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Terminal size={14} color="var(--neon-emerald)" />
          <span>POWERED BY BUN & REACT 19</span>
        </div>
      </footer>
    </div>
  );
}

export default App;
